import lassoLadyOggUrl from './tracks/lasso-lady.ogg';
import lassoLadyAacUrl from './tracks/lasso-lady.m4a';
import unDesertUrl from './tracks/un-desert.m4a';
import type { AudioEngine } from './engine';

/** The two shipped tracks — both CC0, provenance in tracks/LICENSES.md.
 * Bundled as assets: the web build serves them as files, the desktop
 * single-file build inlines them as data: URIs so file:// just works.
 * `urls` is a preference order: Vorbis loops seamlessly but Safari/WebKit
 * can't decode it, so an AAC transcode rides along as the fallback.
 * Artist info feeds the in-game credits page — CC0 asks nothing of us,
 * which is exactly why we say thank you anyway. */
export const MUSIC_TRACKS = [
  {
    id: 'lasso-lady',
    label: 'Lasso Lady (title theme)',
    title: 'Lasso Lady',
    artist: 'congusbongus',
    role: 'Title theme',
    source: 'https://opengameart.org/content/lasso-lady-seamless-loop',
    urls: [lassoLadyOggUrl, lassoLadyAacUrl],
  },
  {
    id: 'un-desert',
    label: 'Un désert (in-game theme)',
    title: 'Un désert',
    artist: 'Loyalty Freak Music',
    role: 'In-game theme',
    source: 'https://opengameart.org/content/un-d%C3%A9sert',
    urls: [unDesertUrl],
  },
] as const;

export type MusicTrackId = (typeof MUSIC_TRACKS)[number]['id'];

export const TITLE_TRACK: MusicTrackId = 'lasso-lady';
export const GAME_TRACK: MusicTrackId = 'un-desert';

/** Loads and loops one track through the music bus, with a short crossfade
 * on switches. Fetch/decode failure degrades to silence (with a console
 * warning), and the next context resume retries — never a thrown error. */
export class MusicPlayer {
  private readonly engine: AudioEngine;
  private readonly cache = new Map<string, AudioBuffer>();
  private source: AudioBufferSourceNode | null = null;
  private sourceGain: GainNode | null = null;
  private current: MusicTrackId | null = null;

  constructor(engine: AudioEngine) {
    this.engine = engine;
  }

  get playing(): MusicTrackId | null {
    return this.current;
  }

  async play(id: MusicTrackId): Promise<void> {
    const ctx = this.engine.ctx;
    const track = MUSIC_TRACKS.find((candidate) => candidate.id === id);
    if (!ctx || !track || this.current === id) return;
    this.current = id;
    let buffer = this.cache.get(id) ?? null;
    if (!buffer) {
      for (const url of track.urls) {
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          buffer = await ctx.decodeAudioData(await response.arrayBuffer());
          this.cache.set(id, buffer);
          break;
        } catch (error) {
          console.warn(`[boomtown] music: could not load ${url}:`, error);
        }
      }
    }
    if (this.current !== id) return; // superseded while loading
    if (!buffer) {
      this.current = null; // stay silent; the next context resume retries
      return;
    }
    this.fadeOutCurrent();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(1, ctx.currentTime + 0.8);
    source.connect(gain);
    gain.connect(this.engine.music);
    source.start();
    this.source = source;
    this.sourceGain = gain;
  }

  stop(): void {
    this.fadeOutCurrent();
    this.current = null;
  }

  private fadeOutCurrent(): void {
    const ctx = this.engine.ctx;
    if (!ctx || !this.source || !this.sourceGain) return;
    const source = this.source;
    const gain = this.sourceGain;
    this.source = null;
    this.sourceGain = null;
    gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
    setTimeout(() => {
      try {
        source.stop();
      } catch {
        // already stopped
      }
    }, 700);
  }
}
